import React, { useState, useCallback, useEffect } from 'react';
import './App.css';
import Board from './components/Board';
import DifficultySelector from './components/DifficultySelector';
import { generateBoard, difficulties } from './utils/boardGenerator';

function App() {
  const [difficulty, setDifficulty] = useState('easy');
  const [board, setBoard] = useState(generateBoard(difficulty));
  const [revealedCells, setRevealedCells] = useState([]);
  const [flaggedCells, setFlaggedCells] = useState([]);
  const [failures, setFailures] = useState(0);
  const [history, setHistory] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);

  const handleNewGame = useCallback(() => {
    setBoard(generateBoard(difficulty));
    setRevealedCells([]);
    setFlaggedCells([]);
    setFailures(0);
    setHistory([]);
    setGameOver(false);
    setGameWon(false);
  }, [difficulty]);

  const revealAdjacentEmptyCells = useCallback((row, col, newRevealed) => {
    const directions = [
      [-1, -1], [-1, 0], [-1, 1],
      [0, -1], [0, 1],
      [1, -1], [1, 0], [1, 1]
    ];

    directions.forEach(([dx, dy]) => {
      const newRow = row + dx;
      const newCol = col + dy;

      if (
        newRow >= 0 && newRow < board.length &&
        newCol >= 0 && newCol < board[0].length &&
        !newRevealed[newRow]?.[newCol]
      ) {
        newRevealed[newRow] = newRevealed[newRow] || [];
        newRevealed[newRow][newCol] = true;

        if (board[newRow][newCol] === 0) {
          revealAdjacentEmptyCells(newRow, newCol, newRevealed);
        }
      }
    });
  }, [board]);

  const checkWinCondition = useCallback(() => {
    const { rows, cols, mines } = difficulties[difficulty];
    let revealedCount = 0;
    let correctFlags = 0;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        if (revealedCells[row]?.[col]) {
          revealedCount++;
        }
        if (flaggedCells[row]?.[col] && board[row][col] === -1) {
          correctFlags++;
        }
      }
    }

    if (revealedCount === rows * cols - mines || correctFlags === mines) {
      setGameWon(true);
      setGameOver(true);
    }
  }, [board, revealedCells, flaggedCells, difficulty]);

  useEffect(() => {
    checkWinCondition();
  }, [revealedCells, flaggedCells, checkWinCondition]);

  const handleCellClick = useCallback((row, col) => {
    if (gameOver || flaggedCells[row]?.[col]) return;

    setHistory(prev => [...prev, { board, revealedCells, flaggedCells, failures, gameOver, gameWon }]);
    
    setRevealedCells(prev => {
      const newRevealed = JSON.parse(JSON.stringify(prev));
      newRevealed[row] = newRevealed[row] || [];
      newRevealed[row][col] = true;

      if (board[row][col] === 0) {
        revealAdjacentEmptyCells(row, col, newRevealed);
      }

      return newRevealed;
    });

    if (board[row][col] === -1) {
      setFailures(prev => prev + 1);
      setGameOver(true);
    }
  }, [board, revealedCells, flaggedCells, failures, gameOver, gameWon, revealAdjacentEmptyCells]);

  const handleCellRightClick = useCallback((e, row, col) => {
    e.preventDefault();
    if (gameOver || revealedCells[row]?.[col]) return;

    setHistory(prev => [...prev, { board, revealedCells, flaggedCells, failures, gameOver, gameWon }]);

    setFlaggedCells(prev => {
      const newFlagged = JSON.parse(JSON.stringify(prev));
      newFlagged[row] = newFlagged[row] || [];
      newFlagged[row][col] = !newFlagged[row][col];
      return newFlagged;
    });
  }, [board, revealedCells, flaggedCells, failures, gameOver, gameWon]);

  const handleUndo = useCallback(() => {
    if (history.length > 0) {
      const lastState = history[history.length - 1];
      setBoard(lastState.board);
      setRevealedCells(lastState.revealedCells);
      setFlaggedCells(lastState.flaggedCells);
      setFailures(lastState.failures);
      setGameOver(lastState.gameOver);
      setGameWon(lastState.gameWon);
      setHistory(prev => prev.slice(0, -1));
    }
  }, [history]);

  return (
    <div className="App">
      <header className="game-header">
        <h1>Minesweeper</h1>
      </header>
      <div className="game-controls">
        <DifficultySelector difficulty={difficulty} setDifficulty={setDifficulty} />
        <button onClick={handleNewGame}>New Game</button>
        <button onClick={handleUndo} disabled={history.length === 0}>Undo</button>
      </div>
      <div className="game-info">
        <p>Failures: {failures}</p>
        {gameOver && !gameWon && <p className="game-message">Game Over! Click Undo to continue or start a New Game.</p>}
        {gameWon && <p className="game-message win">Congratulations! You've won the game!</p>}
      </div>
      <Board 
        board={board} 
        revealedCells={revealedCells}
        flaggedCells={flaggedCells}
        onCellClick={handleCellClick}
        onCellRightClick={handleCellRightClick}
        gameOver={gameOver}
      />
      <footer className="game-footer">
        <p>This game is generated using the Cursor browser and composer</p>
      </footer>
    </div>
  );
}

export default App;
